import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Workflow, Plus, Trash2, Save, Loader2, ArrowRight } from "lucide-react";

// Visual (form-driven) strategy graph builder. MVP: sequential IF chains.
// Node types: source → indicator → compare → action
const NODE_TYPES = ["source", "indicator", "compare", "action"];
const INDICATOR_LABELS = { rsi: "RSI (14)", macd_hist: "MACD hist", ema_cross_20_50: "EMA 20 × 50", price: "Price", volume: "Volume" };
const OPS = [">", "<", ">=", "<=", "cross_up", "cross_down"];
const ACTIONS = ["buy", "sell", "close"];

const DEFAULT_NODES = [
  { id: "n1", type: "source", x: 40, y: 40, config: { symbol: "AAPL", asset_type: "stock" } },
  { id: "n2", type: "indicator", x: 240, y: 40, config: { indicator: "rsi" } },
  { id: "n3", type: "compare", x: 440, y: 40, config: { operator: "<", threshold: 30 } },
  { id: "n4", type: "action", x: 640, y: 40, config: { action: "buy", amount_usd: 500 } },
];
const DEFAULT_EDGES = [{ from: "n1", to: "n2" }, { from: "n2", to: "n3" }, { from: "n3", to: "n4" }];

export default function Strategies() {
  const [graphs, setGraphs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);   // null => creating new
  const [name, setName] = useState("My Strategy");
  const [nodes, setNodes] = useState(DEFAULT_NODES);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/strategies/graphs").then((r) => setGraphs(r.data.graphs || []))
    .catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const editNode = (id, patch) => setNodes((ns) => ns.map((n) => n.id === id ? { ...n, config: { ...n.config, ...patch } } : n));

  const addNode = (type) => setNodes((ns) => [...ns, {
    id: `n${Date.now()}`, type, x: 40, y: 40 + ns.length * 90, config: {},
  }]);

  const removeNode = (id) => setNodes((ns) => ns.length > 1 ? ns.filter((n) => n.id !== id) : ns);

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    const src = nodes.find((n) => n.type === "source");
    if (!src?.config?.symbol) return toast.error("Add a source node with a symbol");
    setSaving(true);
    try {
      // Auto-generate sequential edges: n[i] -> n[i+1]
      const edges = nodes.slice(0, -1).map((n, i) => ({ from: n.id, to: nodes[i + 1].id }));
      const { data } = await api.post("/strategies/graphs", {
        name: name.trim(),
        symbol: src.config.symbol.toUpperCase(),
        asset_type: src.config.asset_type || "stock",
        timeframe: "1h",
        nodes, edges,
      });
      toast.success("Strategy saved");
      setCurrent(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (g) => {
    if (!window.confirm(`Delete "${g.name}"?`)) return;
    await api.delete(`/strategies/graphs/${g.id}`);
    toast.success("Deleted"); load();
  };

  const loadGraph = (g) => {
    setCurrent(g); setName(g.name);
    setNodes(g.nodes || DEFAULT_NODES);
  };

  const newGraph = () => { setCurrent(null); setName("My Strategy"); setNodes(DEFAULT_NODES); };

  return (
    <div className="space-y-6" data-testid="strategies-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">No-code</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
            <Workflow className="w-7 h-7" /> Strategy Builder
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Chain a source → indicator → comparison → action to describe a trading rule visually.
          </p>
        </div>
        <Button onClick={newGraph} variant="outline" data-testid="strat-new" className="rounded-full">
          <Plus className="w-4 h-4 mr-1" /> New strategy
        </Button>
      </div>

      {/* Editor */}
      <Card className="p-5 rounded-2xl" data-testid="strat-editor">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 data-testid="strat-name" placeholder="Strategy name" className="max-w-sm rounded-lg" />
          <div className="flex gap-2 items-center ml-auto">
            {NODE_TYPES.map((t) => (
              <Button key={t} size="sm" variant="outline"
                      onClick={() => addNode(t)}
                      data-testid={`strat-add-${t}`} className="rounded-full">
                <Plus className="w-3 h-3 mr-1" /> {t}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="strat-canvas">
          {nodes.map((n, i) => (
            <div key={n.id} className="flex items-center">
              <NodeCard node={n} onEdit={(patch) => editNode(n.id, patch)} onRemove={() => removeNode(n.id)} testid={`strat-node-${i}`} />
              {i < nodes.length - 1 && <ArrowRight className="w-5 h-5 mx-1 text-muted-foreground" />}
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="strat-save" className="rounded-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save strategy
          </Button>
        </div>
      </Card>

      {/* Saved list */}
      <Card className="p-5 rounded-2xl" data-testid="strat-list">
        <div className="text-lg font-display font-semibold mb-3">Saved strategies</div>
        {loading ? <div className="text-sm text-muted-foreground py-4 text-center">Loading…</div>
          : graphs.length === 0 ? <div className="text-sm text-muted-foreground py-4 text-center" data-testid="strat-empty">None yet — save your first above.</div>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {graphs.map((g) => (
                <div key={g.id} data-testid={`strat-saved-${g.id}`}
                     className="p-3 rounded-lg border border-border flex items-center justify-between">
                  <div>
                    <div className="font-medium">{g.name}</div>
                    <div className="text-xs text-muted-foreground">{(g.symbol || "").toUpperCase()} · {g.nodes?.length || 0} nodes · {new Date(g.updated_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => loadGraph(g)} data-testid={`strat-open-${g.id}`}>Open</Button>
                    <Button size="sm" variant="ghost" onClick={() => del(g)} data-testid={`strat-del-${g.id}`} className="text-[hsl(var(--loss))]">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </Card>
    </div>
  );
}

const NodeCard = ({ node, onEdit, onRemove, testid }) => {
  const c = node.config || {};
  return (
    <div className="p-3 rounded-xl border border-border bg-secondary/30 min-w-[180px] relative" data-testid={testid}>
      <button onClick={onRemove} className="absolute top-1 right-1 text-muted-foreground hover:text-[hsl(var(--loss))]" data-testid={`${testid}-remove`}>
        <Trash2 className="w-3 h-3" />
      </button>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{node.type}</div>
      {node.type === "source" && (
        <>
          <Input value={c.symbol || ""} onChange={(e) => onEdit({ symbol: e.target.value.toUpperCase() })}
                 placeholder="Symbol" data-testid={`${testid}-symbol`} className="h-8 text-sm rounded-md" />
          <select value={c.asset_type || "stock"} onChange={(e) => onEdit({ asset_type: e.target.value })}
                  data-testid={`${testid}-asset`} className="w-full mt-1 h-7 rounded-md border border-input bg-transparent px-2 text-xs">
            <option value="stock">stock</option><option value="crypto">crypto</option>
          </select>
        </>
      )}
      {node.type === "indicator" && (
        <select value={c.indicator || "rsi"} onChange={(e) => onEdit({ indicator: e.target.value })}
                data-testid={`${testid}-ind`} className="w-full h-8 rounded-md border border-input bg-transparent px-2 text-xs">
          {Object.entries(INDICATOR_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      )}
      {node.type === "compare" && (
        <>
          <select value={c.operator || ">"} onChange={(e) => onEdit({ operator: e.target.value })}
                  data-testid={`${testid}-op`} className="w-full h-8 rounded-md border border-input bg-transparent px-2 text-xs mb-1">
            {OPS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <Input type="number" step="any" value={c.threshold ?? ""} onChange={(e) => onEdit({ threshold: Number(e.target.value) })}
                 placeholder="Threshold" data-testid={`${testid}-thr`} className="h-8 text-sm rounded-md" />
        </>
      )}
      {node.type === "action" && (
        <>
          <select value={c.action || "buy"} onChange={(e) => onEdit({ action: e.target.value })}
                  data-testid={`${testid}-action`} className="w-full h-8 rounded-md border border-input bg-transparent px-2 text-xs mb-1">
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <Input type="number" step="any" value={c.amount_usd ?? ""} onChange={(e) => onEdit({ amount_usd: Number(e.target.value) })}
                 placeholder="$ amount" data-testid={`${testid}-amt`} className="h-8 text-sm rounded-md" />
        </>
      )}
    </div>
  );
};
